import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { api, MultiSelectDropdown } from './index';

// ==================== BULK SCRAPE PROGRESS ====================
export const BulkScrapeProgress = ({ token, onComplete }) => {
  const { language } = useLanguage();
  const [status, setStatus] = useState(null);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    loadStatus();
    const interval = setInterval(loadStatus, 5000);
    return () => clearInterval(interval);
  }, []);

  const loadStatus = async () => {
    const res = await api.get('/api/school-finder/bulk-scrape-status', token);
    if (res.success && res.has_job) {
      setStatus(res.job);
      if (res.job.status === 'completed' && onComplete) onComplete();
    } else {
      setStatus(null);
    }
  };

  const startBulkScrape = async () => {
    setStarting(true);
    const res = await api.post('/api/school-finder/bulk-scrape-all-india', {}, token);
    if (res.success) loadStatus();
    else alert(res.message_hi || res.message);
    setStarting(false);
  };

  const stopBulkScrape = async () => {
    if (!window.confirm(language === 'hi' ? 'क्या आप scraping रोकना चाहते हैं?' : 'Stop scraping?')) return;
    await api.post('/api/school-finder/bulk-scrape-stop', {}, token);
    loadStatus();
  };

  if (!status || status.status === 'completed' || status.status === 'stopped') {
    return (
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-blue-800">🇮🇳 {language === 'hi' ? 'All India CBSE Bulk Scrape' : 'All India CBSE Bulk Scrape'}</h3>
            <p className="text-sm text-blue-600 mt-1">{language === 'hi' ? 'सभी 37 राज्यों से CBSE schools extract करें (FREE)' : 'Extract CBSE schools from all 37 states (FREE)'}</p>
            {status?.status === 'completed' && <div className="mt-2 text-sm text-green-700 bg-green-100 px-3 py-1 rounded-full inline-block">✅ Last: {status.total_schools_saved} schools</div>}
          </div>
          <button onClick={startBulkScrape} disabled={starting} className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50">{starting ? '⏳' : '🚀'} {language === 'hi' ? 'शुरू करें' : 'Start'}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-green-800"><span className="animate-pulse">🔄</span> {language === 'hi' ? 'Bulk Scrape चल रही है...' : 'Bulk Scrape Running...'}</h3>
        <button onClick={stopBulkScrape} className="px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600">⏹️ Stop</button>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-4 mb-3 overflow-hidden">
        <div className="bg-gradient-to-r from-green-500 to-emerald-500 h-4 rounded-full" style={{ width: `${status.progress_percent || 0}%` }}><span className="text-xs text-white font-bold">{status.progress_percent || 0}%</span></div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        <div className="bg-white/60 rounded-lg p-2 text-center"><p className="text-2xl font-bold text-blue-600">{status.completed_states || 0}/{status.total_states || 37}</p><p className="text-xs text-gray-600">States</p></div>
        <div className="bg-white/60 rounded-lg p-2 text-center"><p className="text-2xl font-bold text-green-600">{status.total_schools_found?.toLocaleString() || 0}</p><p className="text-xs text-gray-600">Found</p></div>
        <div className="bg-white/60 rounded-lg p-2 text-center"><p className="text-2xl font-bold text-purple-600">{status.total_schools_saved?.toLocaleString() || 0}</p><p className="text-xs text-gray-600">Saved</p></div>
        <div className="bg-white/60 rounded-lg p-2 text-center"><p className="text-2xl font-bold text-gray-600">{status.time_elapsed || '0:00'}</p><p className="text-xs text-gray-600">Time</p></div>
      </div>
      {status.current_state && <div className="bg-yellow-100 rounded-lg p-2"><p className="text-sm"><span className="animate-pulse">⏳</span> {status.current_state}</p></div>}
    </div>
  );
};


// ==================== SARAS ENRICHMENT PROGRESS ====================
export const SARASEnrichmentProgress = ({ token, states = [] }) => {
  const { language } = useLanguage();
  const [status, setStatus] = useState(null);
  const [starting, setStarting] = useState(false);
  const [selectedStates, setSelectedStates] = useState([]);
  const [selectedDistricts, setSelectedDistricts] = useState([]);
  const [stateStats, setStateStats] = useState(null);
  const [allStatesStats, setAllStatesStats] = useState([]);
  const [districtStats, setDistrictStats] = useState([]);
  const [showDistrictFilter, setShowDistrictFilter] = useState(false);

  useEffect(() => {
    loadStatus();
    loadAllStatesStats();
    const interval = setInterval(loadStatus, 3000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (selectedStates.length === 1) {
      loadStateStats(selectedStates[0]);
      loadDistrictStats(selectedStates[0]);
      setShowDistrictFilter(true);
    } else {
      if (selectedStates.length > 1) {
        const combinedStats = allStatesStats
          .filter(s => selectedStates.includes(s.state))
          .reduce((acc, s) => ({ total: acc.total + s.total, enriched: acc.enriched + s.enriched, pending: acc.pending + s.pending }), { total: 0, enriched: 0, pending: 0 });
        setStateStats({ ...combinedStats, state: `${selectedStates.length} states` });
      } else {
        setStateStats(null);
      }
      setShowDistrictFilter(false);
      setSelectedDistricts([]);
      setDistrictStats([]);
    }
  }, [selectedStates, allStatesStats]);

  const loadStatus = async () => {
    const res = await api.get('/api/school-finder/saras-enrichment-status', token);
    if (res.success) setStatus(res);
  };

  const loadAllStatesStats = async () => {
    const res = await api.get('/api/school-finder/saras-all-states-stats', token);
    if (res.success) setAllStatesStats(res.states || []);
  };

  const loadStateStats = async (state) => {
    const res = await api.get(`/api/school-finder/saras-state-stats?state=${state}`, token);
    if (res.success) setStateStats(res.stats);
  };

  const loadDistrictStats = async (state) => {
    const res = await api.get(`/api/school-finder/saras-districts-by-state?state=${state}`, token);
    if (res.success) setDistrictStats(res.districts || []);
  };

  const startEnrichment = async (batchSize = 500) => {
    setStarting(true);
    const requestBody = { states: selectedStates.length > 0 ? selectedStates : null, districts: selectedDistricts.length > 0 ? selectedDistricts : null, batch_size: batchSize };
    const res = await api.post('/api/school-finder/bulk-enrich-saras', requestBody, token);
    if (res.success) {
      loadStatus();
      loadAllStatesStats();
      if (selectedStates.length === 1) loadDistrictStats(selectedStates[0]);
    } else {
      alert(res.message_hi || res.message);
    }
    setStarting(false);
  };

  const stopEnrichment = async () => {
    if (!window.confirm(language === 'hi' ? 'क्या आप enrichment रोकना चाहते हैं?' : 'Stop enrichment?')) return;
    await api.post('/api/school-finder/saras-enrichment-stop', {}, token);
    loadStatus();
  };

  if (!status) return null;

  const job = status.job;
  const stats = status.stats;
  const isRunning = job?.status === 'running';
  const isIdle = job?.status === 'idle' || !job?.status;

  if (stats?.pending === 0) {
    return (
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200">
        <div className="flex items-center gap-2">
          <span className="text-2xl">✅</span>
          <div>
            <p className="font-semibold text-green-800">{language === 'hi' ? 'सभी CBSE Schools Enriched!' : 'All CBSE Schools Enriched!'}</p>
            <p className="text-sm text-green-600">{stats.enriched?.toLocaleString()} schools with detailed SARAS data</p>
          </div>
        </div>
      </div>
    );
  }

  if (isIdle || job?.status === 'completed' || job?.status === 'stopped') {
    return (
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-4 border border-purple-200">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <h3 className="font-semibold text-purple-800">🎓 {language === 'hi' ? 'SARAS Detail Enrichment' : 'SARAS Detail Enrichment'}</h3>
            <p className="text-sm text-purple-600 mt-1">{language === 'hi' ? `${stats?.pending?.toLocaleString()} CBSE schools की detailed info लाएं` : `Fetch detailed info for ${stats?.pending?.toLocaleString()} CBSE schools`}</p>
            
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <label className="text-sm font-medium text-purple-700">{language === 'hi' ? '📍 States चुनें:' : '📍 Select States:'}</label>
              <MultiSelectDropdown options={allStatesStats} selected={selectedStates} onChange={(newStates) => { setSelectedStates(newStates); setSelectedDistricts([]); }} placeholder={language === 'hi' ? `सभी States (${stats?.pending?.toLocaleString()} pending)` : `All States (${stats?.pending?.toLocaleString()} pending)`} language={language} />
              {selectedStates.length > 0 && <span className="text-xs bg-purple-200 text-purple-800 px-2 py-1 rounded-full font-medium">{selectedStates.length} {language === 'hi' ? 'राज्य चुने' : 'selected'}</span>}
            </div>
            
            {showDistrictFilter && districtStats.length > 0 && (
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <label className="text-sm font-medium text-orange-700">{language === 'hi' ? '🏘️ Districts चुनें:' : '🏘️ Select Districts:'}</label>
                <MultiSelectDropdown options={districtStats} selected={selectedDistricts} onChange={setSelectedDistricts} placeholder={language === 'hi' ? `सभी Districts` : `All Districts`} language={language} optionKey="district" />
                {selectedDistricts.length > 0 && <span className="text-xs bg-orange-200 text-orange-800 px-2 py-1 rounded-full font-medium">{selectedDistricts.length} {language === 'hi' ? 'जिले' : 'districts'}</span>}
              </div>
            )}
            
            {selectedStates.length > 0 && selectedStates.length <= 5 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {selectedStates.map(s => (
                  <span key={s} className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs flex items-center gap-1">
                    {s}
                    <button onClick={() => setSelectedStates(selectedStates.filter(st => st !== s))} className="hover:text-red-500 ml-1">✕</button>
                  </span>
                ))}
              </div>
            )}
            
            {stateStats && (
              <div className="mt-2 p-2 bg-white/50 rounded-lg">
                <div className="flex gap-2 mt-1 flex-wrap">
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">📚 Total: {stateStats.total?.toLocaleString()}</span>
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">✅ Enriched: {stateStats.enriched?.toLocaleString()}</span>
                  <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">⏳ Pending: {stateStats.pending?.toLocaleString()}</span>
                </div>
              </div>
            )}
            
            <div className="flex gap-2 mt-2">
              <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">✅ {stats?.enriched?.toLocaleString()} enriched</span>
              <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">⏳ {stats?.pending?.toLocaleString()} pending</span>
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">📊 {stats?.percent_complete}% complete</span>
            </div>
          </div>
          <div className="flex flex-col gap-2 ml-4">
            <button onClick={() => startEnrichment(500)} disabled={starting} className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50 flex items-center gap-2 text-sm">
              {starting ? '⏳' : '🚀'} {language === 'hi' ? 'शुरू (500)' : 'Start (500)'}
            </button>
            <button onClick={() => startEnrichment(2000)} disabled={starting} className="px-3 py-1 bg-purple-400 text-white rounded text-xs hover:bg-purple-500 disabled:opacity-50">
              {language === 'hi' ? 'बड़ा Batch (2000)' : 'Large Batch (2000)'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const progressPercent = job.total_schools > 0 ? Math.round((job.processed / job.total_schools) * 100) : 0;

  return (
    <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-4 border border-purple-200">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-purple-800 flex items-center gap-2"><span className="animate-pulse">🔄</span> {language === 'hi' ? 'SARAS Enrichment चल रही है...' : 'SARAS Enrichment Running...'}</h3>
        <button onClick={stopEnrichment} className="px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600">⏹️ Stop</button>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-4 mb-3 overflow-hidden">
        <div className="bg-gradient-to-r from-purple-500 to-indigo-500 h-4 rounded-full transition-all duration-500 flex items-center justify-center" style={{ width: `${progressPercent}%` }}>
          <span className="text-xs text-white font-bold">{progressPercent}%</span>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        <div className="bg-white/60 rounded-lg p-2 text-center"><p className="text-2xl font-bold text-blue-600">{job.processed}/{job.total_schools}</p><p className="text-xs text-gray-600">Processed</p></div>
        <div className="bg-white/60 rounded-lg p-2 text-center"><p className="text-2xl font-bold text-green-600">{job.enriched}</p><p className="text-xs text-gray-600">Enriched</p></div>
        <div className="bg-white/60 rounded-lg p-2 text-center"><p className="text-2xl font-bold text-red-600">{job.errors}</p><p className="text-xs text-gray-600">Errors</p></div>
        <div className="bg-white/60 rounded-lg p-2 text-center"><p className="text-2xl font-bold text-purple-600">{stats?.pending?.toLocaleString()}</p><p className="text-xs text-gray-600">Pending</p></div>
      </div>
      {job.current_school && <div className="bg-yellow-100 rounded-lg p-2"><p className="text-xs truncate"><span className="animate-pulse">⏳</span> {job.current_school}</p></div>}
    </div>
  );
};


// ==================== UDISE ENRICHMENT PROGRESS ====================
export const UDISEEnrichmentProgress = ({ token }) => {
  const { language } = useLanguage();
  const [status, setStatus] = useState(null);
  const [persistentStatus, setPersistentStatus] = useState(null);
  const [starting, setStarting] = useState(false);
  const [selectedStates, setSelectedStates] = useState([]);
  const [selectedDistricts, setSelectedDistricts] = useState([]);
  const [allStatesStats, setAllStatesStats] = useState([]);
  const [districtStats, setDistrictStats] = useState([]);
  const [stateStats, setStateStats] = useState(null);
  const [showDistrictFilter, setShowDistrictFilter] = useState(false);
  const [showPersistent, setShowPersistent] = useState(true);

  useEffect(() => {
    loadStatus();
    loadPersistentStatus();
    loadAllStatesStats();
    const interval = setInterval(() => { loadStatus(); loadPersistentStatus(); }, 3000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (selectedStates.length === 1) {
      loadDistrictStats(selectedStates[0]);
      setShowDistrictFilter(true);
      const singleStateStats = allStatesStats.find(s => s.state === selectedStates[0]);
      if (singleStateStats) setStateStats(singleStateStats);
    } else if (selectedStates.length > 1) {
      const combinedStats = allStatesStats
        .filter(s => selectedStates.includes(s.state))
        .reduce((acc, s) => ({ total: acc.total + s.total, enriched: acc.enriched + s.enriched, pending: acc.pending + s.pending }), { total: 0, enriched: 0, pending: 0 });
      setStateStats({ ...combinedStats, state: `${selectedStates.length} states` });
      setShowDistrictFilter(false);
      setSelectedDistricts([]);
      setDistrictStats([]);
    } else {
      setStateStats(null);
      setShowDistrictFilter(false);
      setSelectedDistricts([]);
      setDistrictStats([]);
    }
  }, [selectedStates, allStatesStats]);

  const loadStatus = async () => {
    const res = await api.get('/api/school-finder/udise-enrichment-status', token);
    if (res.success) setStatus(res);
  };

  const loadPersistentStatus = async () => {
    const res = await api.get('/api/school-finder/persistent-enrichment-status', token);
    if (res.success) setPersistentStatus(res);
  };

  const loadAllStatesStats = async () => {
    const res = await api.get('/api/school-finder/udise-all-states-stats', token);
    if (res.success) setAllStatesStats(res.states || []);
  };

  const loadDistrictStats = async (state) => {
    const res = await api.get(`/api/school-finder/udise-districts-by-state?state=${state}`, token);
    if (res.success) setDistrictStats(res.districts || []);
  };

  const startEnrichment = async (batchSize = 200, reEnrich = false) => {
    setStarting(true);
    const requestBody = { states: selectedStates.length > 0 ? selectedStates : null, districts: selectedDistricts.length > 0 ? selectedDistricts : null, batch_size: batchSize, re_enrich: reEnrich };
    const res = await api.post('/api/school-finder/start-udise-enrichment', requestBody, token);
    if (res.success) {
      loadStatus();
      loadAllStatesStats();
      if (selectedStates.length === 1) loadDistrictStats(selectedStates[0]);
    } else {
      alert(res.message_hi || res.message);
    }
    setStarting(false);
  };

  const startAutoEnrichment = async (state = null) => {
    const stateMsg = state ? `"${state}"` : 'सभी states';
    if (!window.confirm(language === 'hi' ? `🤖 Auto UDISE+ Enrichment शुरू करें?\n\n${stateMsg} के सभी pending schools को automatically enrich करेगा।` : `🤖 Start Auto UDISE+ Enrichment for ${stateMsg}?`)) return;
    
    setStarting(true);
    const url = state ? `/api/school-finder/start-auto-udise-enrichment?state=${encodeURIComponent(state)}` : '/api/school-finder/start-auto-udise-enrichment';
    const res = await api.post(url, {}, token);
    if (res.success) { loadStatus(); loadAllStatesStats(); alert(res.message_hi || res.message); }
    else alert(res.message_hi || res.message);
    setStarting(false);
  };

  const stopEnrichment = async () => {
    if (!window.confirm(language === 'hi' ? 'क्या आप UDISE+ enrichment रोकना चाहते हैं?' : 'Stop UDISE+ enrichment?')) return;
    await api.post('/api/school-finder/udise-enrichment-stop', {}, token);
    loadStatus();
  };

  const startPersistentEnrichment = async () => {
    const statesInfo = selectedStates.length > 0 ? `${selectedStates.length} selected states` : 'सभी states';
    if (!window.confirm(language === 'hi' ? `🚀 Persistent UDISE+ Enrichment शुरू करें?\n\n${statesInfo} को एक-एक state करके enrich करेगा।` : `🚀 Start Persistent UDISE+ Enrichment for ${statesInfo}?`)) return;
    
    setStarting(true);
    const res = await api.post('/api/school-finder/start-persistent-enrichment', { states: selectedStates.length > 0 ? selectedStates : null }, token);
    if (res.success) { loadPersistentStatus(); alert(res.message_hi || 'Started!'); }
    else alert(res.message_hi || res.message);
    setStarting(false);
  };

  const stopPersistentEnrichment = async () => {
    if (!window.confirm(language === 'hi' ? 'रोकें?' : 'Stop?')) return;
    const res = await api.post('/api/school-finder/stop-persistent-enrichment', {}, token);
    alert(res.message_hi || res.message);
    loadPersistentStatus();
  };

  const resumePersistentEnrichment = async () => {
    setStarting(true);
    const res = await api.post('/api/school-finder/resume-persistent-enrichment', {}, token);
    if (res.success) { loadPersistentStatus(); alert(res.message_hi || 'Resumed!'); }
    else alert(res.message_hi || res.message);
    setStarting(false);
  };

  if (!status) return null;

  const job = status.job;
  const stats = status.stats;
  const isRunning = job?.status === 'running';
  const isIdle = job?.status === 'idle' || !job?.status;
  
  const pJob = persistentStatus?.job;
  const pIsRunning = pJob?.status === 'running' && !pJob?.stop_requested;
  const pIsStopped = pJob?.status === 'stopped' || pJob?.stop_requested;
  const pIsCompleted = pJob?.status === 'completed';

  if (showPersistent) {
    return (
      <div className="bg-gradient-to-r from-violet-50 to-purple-50 rounded-xl p-4 border border-violet-200">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-violet-800 flex items-center gap-2">🚀 {language === 'hi' ? 'Persistent UDISE+ Enrichment' : 'Persistent UDISE+ Enrichment'}</h3>
          <button onClick={() => setShowPersistent(false)} className="text-xs text-gray-500 hover:text-gray-700">{language === 'hi' ? 'पुराना UI' : 'Old UI'}</button>
        </div>
        
        <div className="mb-4 p-3 bg-white rounded-lg border border-violet-200">
          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-sm font-medium text-violet-700">{language === 'hi' ? '📍 States Filter:' : '📍 Filter by States:'}</label>
            <MultiSelectDropdown options={allStatesStats} selected={selectedStates} onChange={(newStates) => { setSelectedStates(newStates); setSelectedDistricts([]); }} placeholder={language === 'hi' ? `सभी States (${stats?.pending?.toLocaleString() || 0} pending)` : `All States (${stats?.pending?.toLocaleString() || 0} pending)`} language={language} />
            {selectedStates.length > 0 && <span className="text-xs bg-violet-200 text-violet-800 px-2 py-1 rounded-full font-medium">{selectedStates.length} {language === 'hi' ? 'राज्य' : 'selected'}</span>}
          </div>
          
          {showDistrictFilter && districtStats.length > 0 && (
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <label className="text-sm font-medium text-orange-700">{language === 'hi' ? '🏘️ Districts:' : '🏘️ Districts:'}</label>
              <MultiSelectDropdown options={districtStats} selected={selectedDistricts} onChange={setSelectedDistricts} placeholder={language === 'hi' ? `सभी Districts` : `All Districts`} language={language} optionKey="district" />
            </div>
          )}
          
          {stateStats && (
            <div className="mt-2 text-xs text-violet-600 bg-violet-50 px-2 py-1 rounded">
              📊 {stateStats.state}: {stateStats.total?.toLocaleString()} schools, {stateStats.enriched?.toLocaleString()} enriched, {stateStats.pending?.toLocaleString()} pending
            </div>
          )}
        </div>
        
        {pJob && (
          <div className="mb-4">
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${pIsRunning ? 'bg-green-100 text-green-700 animate-pulse' : pIsStopped ? 'bg-yellow-100 text-yellow-700' : pIsCompleted ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}>
                {pIsRunning ? '🔄 Running' : pIsStopped ? '⏸️ Stopped' : pIsCompleted ? '✅ Completed' : '⏹️ Idle'}
              </span>
              {pJob.current_state && pIsRunning && <span className="text-sm text-violet-600 font-medium">📍 Current: <strong>{pJob.current_state}</strong></span>}
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
              <div className="bg-white rounded-lg p-2 text-center border"><div className="text-lg font-bold text-violet-600">{(pJob.processed || 0).toLocaleString()}</div><div className="text-xs text-gray-500">Processed</div></div>
              <div className="bg-white rounded-lg p-2 text-center border"><div className="text-lg font-bold text-green-600">{(pJob.enriched || 0).toLocaleString()}</div><div className="text-xs text-gray-500">Enriched</div></div>
              <div className="bg-white rounded-lg p-2 text-center border"><div className="text-lg font-bold text-red-600">{(pJob.errors || 0).toLocaleString()}</div><div className="text-xs text-gray-500">Errors</div></div>
              <div className="bg-white rounded-lg p-2 text-center border"><div className="text-lg font-bold text-blue-600">{(pJob.completed_states?.length || 0)}</div><div className="text-xs text-gray-500">States Done</div></div>
            </div>
            
            {pJob.completed_states?.length > 0 && (
              <div className="mb-3">
                <p className="text-xs text-gray-600 mb-1">✅ Completed States:</p>
                <div className="flex flex-wrap gap-1">
                  {pJob.completed_states.map(state => <span key={state} className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">{state}</span>)}
                </div>
              </div>
            )}
          </div>
        )}
        
        <div className="flex gap-2">
          {!pIsRunning && !pIsCompleted && (
            <button onClick={startPersistentEnrichment} disabled={starting} className="px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50 flex items-center gap-2">
              {starting ? '⏳' : '🚀'} Start
            </button>
          )}
          {pIsStopped && !pIsCompleted && (
            <button onClick={resumePersistentEnrichment} disabled={starting} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2">
              {starting ? '⏳' : '▶️'} Resume
            </button>
          )}
          {pIsRunning && <button onClick={stopPersistentEnrichment} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2">⏹️ Stop</button>}
          {pIsCompleted && <div className="flex items-center gap-2 text-green-600 font-medium">✅ All States Complete!</div>}
        </div>
      </div>
    );
  }

  if (stats?.pending === 0 && stats?.total_udise > 0) {
    return (
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-2xl">✅</span>
            <div>
              <p className="font-semibold text-green-800">{language === 'hi' ? 'सभी UDISE+ Schools Enriched!' : 'All UDISE+ Schools Enriched!'}</p>
              <p className="text-sm text-green-600">{stats.enriched?.toLocaleString()} schools with detailed UDISE+ data</p>
            </div>
          </div>
          <button onClick={() => startEnrichment(500, true)} disabled={starting} className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50" data-testid="re-enrich-full-data-btn">
            {starting ? '⏳' : '🔄'} {language === 'hi' ? 'Full Data Extract' : 'Extract Full Data'}
          </button>
        </div>
      </div>
    );
  }

  if (isIdle || job?.status === 'completed' || job?.status === 'stopped') {
    return (
      <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl p-4 border border-teal-200" data-testid="udise-enrichment-section">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <h3 className="font-semibold text-teal-800">🏫 {language === 'hi' ? 'UDISE+ Detail Enrichment' : 'UDISE+ Detail Enrichment'}</h3>
            <p className="text-sm text-teal-600 mt-1">{language === 'hi' ? `${stats?.pending?.toLocaleString() || 0} schools की detailed info लाएं` : `Fetch detailed info for ${stats?.pending?.toLocaleString() || 0} schools`}</p>
            
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <label className="text-sm font-medium text-teal-700">{language === 'hi' ? '📍 States चुनें:' : '📍 Select States:'}</label>
              <MultiSelectDropdown options={allStatesStats} selected={selectedStates} onChange={(newStates) => { setSelectedStates(newStates); setSelectedDistricts([]); }} placeholder={`All States (${stats?.pending?.toLocaleString() || 0} pending)`} language={language} />
            </div>
            
            <div className="flex gap-2 mt-2">
              <span className="text-xs bg-teal-100 text-teal-700 px-2 py-1 rounded">✅ {stats?.enriched?.toLocaleString() || 0} enriched</span>
              <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">⏳ {stats?.pending?.toLocaleString() || 0} pending</span>
            </div>
          </div>
          <div className="flex flex-col gap-2 ml-4">
            <button onClick={() => startAutoEnrichment(selectedStates.length === 1 ? selectedStates[0] : null)} disabled={starting} className="px-4 py-2 bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-lg hover:from-purple-600 hover:to-indigo-600 disabled:opacity-50 flex items-center gap-2 text-sm font-medium shadow-md" data-testid="start-auto-udise-btn">
              {starting ? '⏳' : '🤖'} Auto
            </button>
            <button onClick={() => startEnrichment(200)} disabled={starting} className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 disabled:opacity-50 flex items-center gap-2 text-sm" data-testid="start-udise-enrichment-btn">
              {starting ? '⏳' : '🚀'} Batch (200)
            </button>
          </div>
        </div>
      </div>
    );
  }

  const progressPercent = job.total_schools > 0 ? Math.round((job.processed / job.total_schools) * 100) : 0;

  return (
    <div className={`rounded-xl p-4 border ${job.mode === 'auto' ? 'bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200' : 'bg-gradient-to-r from-teal-50 to-cyan-50 border-teal-200'}`} data-testid="udise-enrichment-running">
      <div className="flex items-center justify-between mb-3">
        <h3 className={`font-semibold flex items-center gap-2 ${job.mode === 'auto' ? 'text-purple-800' : 'text-teal-800'}`}>
          <span className="animate-pulse">🔄</span>
          {job.mode === 'auto' ? '🤖 Auto UDISE+ Enrichment...' : 'UDISE+ Enrichment Running...'}
        </h3>
        <button onClick={stopEnrichment} className="px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600" data-testid="stop-udise-enrichment-btn">⏹️ Stop</button>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-4 mb-3 overflow-hidden">
        <div className={`h-4 rounded-full transition-all duration-500 flex items-center justify-center ${job.mode === 'auto' ? 'bg-gradient-to-r from-purple-500 to-indigo-500' : 'bg-gradient-to-r from-teal-500 to-cyan-500'}`} style={{ width: `${progressPercent}%` }}>
          <span className="text-xs text-white font-bold">{progressPercent}%</span>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        <div className="bg-white/60 rounded-lg p-2 text-center"><p className="text-2xl font-bold text-blue-600">{(job.total_processed || job.processed || 0).toLocaleString()}</p><p className="text-xs text-gray-600">Processed</p></div>
        <div className="bg-white/60 rounded-lg p-2 text-center"><p className="text-2xl font-bold text-green-600">{(job.total_enriched || job.enriched || 0).toLocaleString()}</p><p className="text-xs text-gray-600">Enriched</p></div>
        <div className="bg-white/60 rounded-lg p-2 text-center"><p className="text-2xl font-bold text-red-600">{(job.total_errors || job.errors || 0).toLocaleString()}</p><p className="text-xs text-gray-600">Errors</p></div>
        <div className="bg-white/60 rounded-lg p-2 text-center"><p className="text-2xl font-bold text-orange-600">{(job.total_pending || stats?.pending || 0).toLocaleString()}</p><p className="text-xs text-gray-600">Pending</p></div>
      </div>
      {job.current_school && <div className="bg-yellow-100 rounded-lg p-2"><p className="text-xs truncate"><span className="animate-pulse">⏳</span> {job.current_school}</p></div>}
    </div>
  );
};


// ==================== DATA MERGE PROGRESS ====================
export const DataMergeProgress = ({ token, onComplete }) => {
  const { language } = useLanguage();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [merging, setMerging] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { loadStats(); }, []);

  const loadStats = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/school-finder/merge-stats', token);
      if (res.success) setStats(res);
    } catch (err) { console.error('Failed to load merge stats'); }
    setLoading(false);
  };

  const runMerge = async (dryRun = true) => {
    setMerging(true);
    setResult(null);
    try {
      const res = await api.post('/api/school-finder/run-data-merge', { pass_type: 'all', dry_run: dryRun }, token);
      setResult(res);
      if (!dryRun && res.success && onComplete) { loadStats(); onComplete(); }
    } catch (err) { setResult({ success: false, error: 'Merge failed' }); }
    setMerging(false);
  };

  if (loading) return <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-4 border border-emerald-200"><p className="text-center text-gray-600">Loading...</p></div>;

  return (
    <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-4 border border-emerald-200" data-testid="data-merge-section">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-semibold text-emerald-800 flex items-center gap-2">🔄 {language === 'hi' ? 'Data Merge' : 'Data Merge'}</h3>
          <p className="text-xs text-emerald-600 mt-1">{language === 'hi' ? 'Phone → WhatsApp convert करें' : 'Convert phone to WhatsApp'}</p>
        </div>
        <button onClick={loadStats} className="text-emerald-600 hover:text-emerald-800">🔄</button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div className="bg-white rounded-lg p-3 text-center"><p className="text-2xl font-bold text-emerald-600">{stats.current_stats?.with_whatsapp?.toLocaleString() || 0}</p><p className="text-xs text-gray-600">WhatsApp</p></div>
          <div className="bg-white rounded-lg p-3 text-center"><p className="text-2xl font-bold text-blue-600">{stats.current_stats?.map_ready?.toLocaleString() || 0}</p><p className="text-xs text-gray-600">Map Ready</p></div>
          <div className="bg-white rounded-lg p-3 text-center border-2 border-amber-300"><p className="text-2xl font-bold text-amber-600">{stats.merge_opportunities?.phone_to_whatsapp?.toLocaleString() || 0}</p><p className="text-xs text-gray-600">📱 Can Merge</p></div>
          <div className="bg-white rounded-lg p-3 text-center"><p className="text-2xl font-bold text-gray-600">{stats.current_stats?.map_ready_percent || 0}%</p><p className="text-xs text-gray-600">Coverage</p></div>
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        <button onClick={() => runMerge(true)} disabled={merging} className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 text-sm" data-testid="merge-dry-run-btn">
          {merging ? '⏳' : '🔍'} Preview
        </button>
        <button onClick={() => { if (window.confirm(language === 'hi' ? 'Data merge करें?' : 'Run merge?')) runMerge(false); }} disabled={merging} className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 text-sm" data-testid="merge-run-btn">
          {merging ? '⏳ Merging...' : '🚀'} Merge
        </button>
      </div>

      {result && (
        <div className={`mt-4 p-3 rounded-lg ${result.success ? 'bg-green-100' : 'bg-red-100'}`}>
          <p className="font-semibold text-sm">{result.dry_run ? '🔍 Preview:' : '✅ Done:'}</p>
          {result.passes?.phone_consolidation && <p className="text-sm mt-1">📱 Phone → WhatsApp: <strong>{result.passes.phone_consolidation.consolidated}</strong></p>}
          {result.improvements && (
            <div className="mt-2 p-2 bg-green-200 rounded text-sm">
              <p>✨ New WhatsApp: <strong>+{result.improvements.new_whatsapp}</strong></p>
              <p>📍 New Map Ready: <strong>+{result.improvements.new_map_ready}</strong></p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};


// ==================== GOOGLE ENRICHMENT PROGRESS ====================
export const GoogleEnrichmentProgress = ({ token }) => {
  const { language } = useLanguage();
  const [status, setStatus] = useState(null);
  const [starting, setStarting] = useState(false);
  const [allStatesStats, setAllStatesStats] = useState([]);
  const [districtStats, setDistrictStats] = useState([]);
  const [selectedState, setSelectedState] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [batchSize, setBatchSize] = useState(50);

  useEffect(() => {
    loadStatus();
    loadStatesStats();
    const interval = setInterval(loadStatus, 3000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (selectedState) { loadDistrictStats(selectedState); setSelectedDistrict(''); }
  }, [selectedState]);

  const loadStatus = async () => {
    const res = await api.get('/api/school-finder/google-enrichment-status', token);
    if (res.success) setStatus(res);
  };

  const loadStatesStats = async () => {
    const res = await api.get('/api/school-finder/google-enrichment-states', token);
    if (res.success) setAllStatesStats(res.states || []);
  };

  const loadDistrictStats = async (state) => {
    const res = await api.get(`/api/school-finder/google-enrichment-districts?state=${state}`, token);
    if (res.success) setDistrictStats(res.districts || []);
  };

  const startEnrichment = async () => {
    if (!selectedState) { alert(language === 'hi' ? 'State चुनें' : 'Select state'); return; }
    if (!window.confirm(language === 'hi' ? `${batchSize} schools की Google enrichment शुरू करें?\n⚠️ API cost ~$${(batchSize * 0.034).toFixed(2)}` : `Start Google enrichment for ${batchSize} schools?\n⚠️ API cost ~$${(batchSize * 0.034).toFixed(2)}`)) return;
    
    setStarting(true);
    const params = new URLSearchParams({ state: selectedState, limit: batchSize });
    if (selectedDistrict) params.append('district', selectedDistrict);
    
    const res = await api.post(`/api/school-finder/bulk-enrich-google?${params}`, {}, token);
    if (res.success) { loadStatus(); loadStatesStats(); }
    else alert(res.message_hi || res.message);
    setStarting(false);
  };

  const stopEnrichment = async () => {
    if (!window.confirm(language === 'hi' ? 'Google enrichment रोकें?' : 'Stop Google enrichment?')) return;
    await api.post('/api/school-finder/google-enrichment-stop', {}, token);
    loadStatus();
  };

  const job = status?.job;
  const stats = status?.stats;
  const isRunning = job?.status === 'running';

  if (isRunning) {
    const progressPercent = job.total_schools > 0 ? Math.round((job.processed / job.total_schools) * 100) : 0;
    return (
      <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-xl p-4 border border-red-200" data-testid="google-enrichment-running">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-red-800 flex items-center gap-2"><span className="animate-pulse">🔄</span> Google Enrichment Running...</h3>
          <button onClick={stopEnrichment} className="px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600">⏹️ Stop</button>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-4 mb-3 overflow-hidden">
          <div className="bg-gradient-to-r from-red-500 to-orange-500 h-4 rounded-full" style={{ width: `${progressPercent}%` }}>
            <span className="text-xs text-white font-bold">{progressPercent}%</span>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white/60 rounded-lg p-2 text-center"><p className="text-2xl font-bold text-blue-600">{job.processed}/{job.total_schools}</p><p className="text-xs text-gray-600">Processed</p></div>
          <div className="bg-white/60 rounded-lg p-2 text-center"><p className="text-2xl font-bold text-green-600">{job.enriched}</p><p className="text-xs text-gray-600">Enriched</p></div>
          <div className="bg-white/60 rounded-lg p-2 text-center"><p className="text-2xl font-bold text-gray-600">{job.not_found}</p><p className="text-xs text-gray-600">Not Found</p></div>
          <div className="bg-white/60 rounded-lg p-2 text-center"><p className="text-2xl font-bold text-red-600">${job.api_cost?.toFixed(2) || '0.00'}</p><p className="text-xs text-gray-600">Cost</p></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-xl p-4 border border-red-200" data-testid="google-enrichment-section">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-semibold text-red-800">🔍 Google Places Enrichment</h3>
          <p className="text-xs text-red-600 mt-1">⚠️ Paid API (~$0.034/school)</p>
        </div>
        <button onClick={loadStatus} className="text-red-600 hover:text-red-800">🔄</button>
      </div>

      {stats && (
        <div className="grid grid-cols-3 gap-2 mb-4 text-center text-sm">
          <div className="bg-white rounded-lg p-2"><p className="font-bold text-green-600">{stats.total_google_enriched?.toLocaleString() || 0}</p><p className="text-xs text-gray-600">Enriched</p></div>
          <div className="bg-white rounded-lg p-2"><p className="font-bold text-orange-600">{stats.pending?.toLocaleString() || 0}</p><p className="text-xs text-gray-600">Pending</p></div>
          <div className="bg-white rounded-lg p-2"><p className="font-bold text-blue-600">{stats.percent_complete || 0}%</p><p className="text-xs text-gray-600">Complete</p></div>
        </div>
      )}

      <div className="grid grid-cols-3 gap-2 mb-3">
        <select value={selectedState} onChange={(e) => setSelectedState(e.target.value)} className="px-3 py-2 border rounded-lg text-sm" data-testid="google-state-select">
          <option value="">{language === 'hi' ? 'State चुनें' : 'Select State'}</option>
          {allStatesStats.map(s => <option key={s.state} value={s.state}>{s.state} ({s.pending})</option>)}
        </select>
        <select value={selectedDistrict} onChange={(e) => setSelectedDistrict(e.target.value)} className="px-3 py-2 border rounded-lg text-sm" disabled={!selectedState} data-testid="google-district-select">
          <option value="">{language === 'hi' ? 'All Districts' : 'All Districts'}</option>
          {districtStats.map(d => <option key={d.district} value={d.district}>{d.district} ({d.pending})</option>)}
        </select>
        <select value={batchSize} onChange={(e) => setBatchSize(parseInt(e.target.value))} className="px-3 py-2 border rounded-lg text-sm" data-testid="google-batch-select">
          <option value="25">25 (~$0.85)</option>
          <option value="50">50 (~$1.70)</option>
          <option value="100">100 (~$3.40)</option>
          <option value="200">200 (~$6.80)</option>
        </select>
      </div>

      <div className="flex justify-end">
        <button onClick={startEnrichment} disabled={starting || !selectedState} className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm" data-testid="start-google-enrichment-btn">
          {starting ? <><span className="animate-spin">⏳</span> Starting...</> : <>🚀 Start Google Enrichment</>}
        </button>
      </div>
    </div>
  );
};


// ==================== MASTER CONSOLIDATION ====================
export const MasterConsolidation = ({ token }) => {
  const { language } = useLanguage();
  const [stats, setStats] = useState(null);
  const [starting, setStarting] = useState(false);
  const [consolidationType, setConsolidationType] = useState('fill_from_geo');
  const [batchSize, setBatchSize] = useState(1000);
  const [dryRun, setDryRun] = useState(true);

  useEffect(() => {
    loadStats();
    const interval = setInterval(loadStats, 3000);
    return () => clearInterval(interval);
  }, []);

  const loadStats = async () => {
    try {
      const res = await api.get('/api/school-finder/master-consolidation-stats', token);
      if (res.success) setStats(res);
    } catch (err) { console.error('Error loading stats:', err); }
  };

  const startConsolidation = async () => {
    if (!window.confirm(dryRun ? 'Preview mode?' : '⚠️ Live mode?')) return;
    setStarting(true);
    const params = new URLSearchParams({ consolidation_type: consolidationType, batch_size: batchSize, dry_run: dryRun });
    const res = await api.post(`/api/school-finder/run-master-consolidation?${params}`, {}, token);
    if (res.success) loadStats();
    else alert(res.message_hi || res.message);
    setStarting(false);
  };

  const stopConsolidation = async () => {
    if (!window.confirm(language === 'hi' ? 'Consolidation रोकें?' : 'Stop?')) return;
    await api.post('/api/school-finder/master-consolidation-stop', {}, token);
    loadStats();
  };

  if (!stats) {
    return (
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-200" data-testid="master-consolidation-loading">
        <div className="flex items-center gap-2"><span className="animate-spin">⏳</span><span className="text-indigo-700">Loading...</span></div>
      </div>
    );
  }

  const job = stats.job;
  const quality = stats.data_quality;
  const isRunning = job?.status === 'running';

  if (isRunning) {
    const progressPercent = job.total_records > 0 ? Math.round((job.processed / job.total_records) * 100) : 0;
    return (
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-200" data-testid="master-consolidation-running">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-indigo-800 flex items-center gap-2"><span className="animate-pulse">🔄</span> Consolidation Running...</h3>
          <button onClick={stopConsolidation} className="px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600" data-testid="stop-consolidation-btn">⏹️ Stop</button>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-4 mb-3 overflow-hidden">
          <div className="bg-gradient-to-r from-indigo-500 to-purple-500 h-4 rounded-full" style={{ width: `${progressPercent}%` }}>
            <span className="text-xs text-white font-bold">{progressPercent}%</span>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white/60 rounded-lg p-2 text-center"><p className="text-xl font-bold text-blue-600">{job.processed}/{job.total_records}</p><p className="text-xs text-gray-600">Processed</p></div>
          <div className="bg-white/60 rounded-lg p-2 text-center"><p className="text-xl font-bold text-green-600">{job.updated}</p><p className="text-xs text-gray-600">Updated</p></div>
          <div className="bg-white/60 rounded-lg p-2 text-center"><p className="text-xl font-bold text-red-600">{job.errors}</p><p className="text-xs text-gray-600">Errors</p></div>
          <div className="bg-white/60 rounded-lg p-2 text-center"><p className="text-xl font-bold text-purple-600">{job.consolidation_type}</p><p className="text-xs text-gray-600">Type</p></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-200" data-testid="master-consolidation-section">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-semibold text-indigo-800 flex items-center gap-2">🗃️ Master Data Consolidation</h3>
          <p className="text-xs text-indigo-600 mt-1">{language === 'hi' ? 'Data clean करें और missing fields भरें' : 'Clean data and fill missing fields'}</p>
        </div>
        <button onClick={loadStats} className="text-indigo-600 hover:text-indigo-800">🔄</button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
        <div className="bg-white rounded-lg p-2 text-center border border-red-200"><p className="text-lg font-bold text-red-600">{quality?.missing_district?.toLocaleString()}</p><p className="text-xs text-gray-600">No District</p></div>
        <div className="bg-white rounded-lg p-2 text-center border border-orange-200"><p className="text-lg font-bold text-orange-600">{quality?.unknown_board?.toLocaleString()}</p><p className="text-xs text-gray-600">Unknown Board</p></div>
        <div className="bg-white rounded-lg p-2 text-center border border-green-200"><p className="text-lg font-bold text-green-600">{quality?.with_location?.toLocaleString()}</p><p className="text-xs text-gray-600">Has Location</p></div>
        <div className="bg-white rounded-lg p-2 text-center border border-blue-200"><p className="text-lg font-bold text-blue-600">{quality?.with_contact?.toLocaleString()}</p><p className="text-xs text-gray-600">Has Contact</p></div>
        <div className="bg-white rounded-lg p-2 text-center border border-purple-200"><p className="text-lg font-bold text-purple-600">{stats.source_stats?.with_udise_code?.toLocaleString()}</p><p className="text-xs text-gray-600">UDISE Code</p></div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <div>
          <label className="text-xs font-medium text-indigo-700 mb-1 block">🛠️ Type</label>
          <select value={consolidationType} onChange={(e) => setConsolidationType(e.target.value)} className="w-full px-3 py-2 border border-indigo-200 rounded-lg text-sm" data-testid="consolidation-type-select">
            <option value="fill_from_geo">📍 Fill District from Geo Portal</option>
            <option value="fill_board">📚 Determine Board</option>
            <option value="clean_data">🧹 Clean & Normalize Data</option>
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-gray-700 mb-1 block">📦 Batch Size</label>
          <select value={batchSize} onChange={(e) => setBatchSize(parseInt(e.target.value))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" data-testid="consolidation-batch-select">
            <option value="500">500</option>
            <option value="1000">1,000</option>
            <option value="2000">2,000</option>
            <option value="5000">5,000</option>
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-gray-700 mb-1 block">⚙️ Mode</label>
          <div className="flex gap-2">
            <button onClick={() => setDryRun(true)} className={`flex-1 px-3 py-2 rounded-lg text-sm transition ${dryRun ? 'bg-yellow-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-testid="dry-run-btn">🔍 Preview</button>
            <button onClick={() => setDryRun(false)} className={`flex-1 px-3 py-2 rounded-lg text-sm transition ${!dryRun ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-testid="live-mode-btn">✅ Live</button>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button onClick={startConsolidation} disabled={starting} className={`px-4 py-2 rounded-lg flex items-center gap-2 text-sm ${dryRun ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-indigo-500 hover:bg-indigo-600'} text-white disabled:opacity-50`} data-testid="start-consolidation-btn">
          {starting ? <><span className="animate-spin">⏳</span> Starting...</> : <>🚀 {dryRun ? 'Preview' : 'Start Consolidation'}</>}
        </button>
      </div>
    </div>
  );
};


// ==================== GEO PORTAL IMPORT ====================
export const GeoPortalImport = ({ token }) => {
  const { language } = useLanguage();
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState('');
  const [status, setStatus] = useState(null);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    loadStates();
    loadStatus();
    const interval = setInterval(loadStatus, 5000);
    return () => clearInterval(interval);
  }, []);

  const loadStates = async () => {
    const res = await api.get('/api/school-finder/geo-portal/states', token);
    if (res.success) setStates(res.states || []);
  };

  const loadStatus = async () => {
    const res = await api.get('/api/school-finder/geo-portal/import-status', token);
    if (res.success) setStatus(res.job);
  };

  const startImport = async () => {
    if (!selectedState) { alert('Select a state'); return; }
    setStarting(true);
    const state = states.find(s => s.code === selectedState);
    const res = await api.post('/api/school-finder/geo-portal/start-import', { state: state?.name, private_only: true }, token);
    if (res.success) loadStatus();
    else alert(res.error);
    setStarting(false);
  };

  const stopImport = async () => {
    await api.post('/api/school-finder/geo-portal/stop-import', {}, token);
    loadStatus();
  };

  return (
    <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-4 border border-emerald-200">
      <h3 className="font-semibold text-emerald-800 mb-3">🗺️ Geo Portal Import (schoolgis.nic.in)</h3>
      
      {status?.status === 'running' ? (
        <div>
          <div className="w-full bg-gray-200 rounded-full h-3 mb-3">
            <div className="bg-emerald-500 h-3 rounded-full" style={{ width: `${(status.fetched / (status.total || 1)) * 100}%` }}></div>
          </div>
          <div className="grid grid-cols-4 gap-2 text-center text-sm mb-2">
            <div><p className="font-bold">{status.fetched}/{status.total}</p><p className="text-xs">Fetched</p></div>
            <div><p className="font-bold text-green-600">{status.imported}</p><p className="text-xs">New</p></div>
            <div><p className="font-bold text-blue-600">{status.updated}</p><p className="text-xs">Updated</p></div>
            <div><p className="font-bold text-gray-600">{status.skipped}</p><p className="text-xs">Skipped</p></div>
          </div>
          <button onClick={stopImport} className="w-full py-2 bg-red-500 text-white rounded">⏹️ Stop</button>
        </div>
      ) : (
        <div>
          <select value={selectedState} onChange={(e) => setSelectedState(e.target.value)} className="w-full px-3 py-2 border rounded text-sm mb-2">
            <option value="">Select State</option>
            {states.map(s => <option key={s.code} value={s.code}>{s.name} {s.imported > 0 ? `(${s.imported} imported)` : ''}</option>)}
          </select>
          <button onClick={startImport} disabled={starting || !selectedState} className="w-full py-2 bg-emerald-500 text-white rounded disabled:opacity-50">{starting ? '⏳' : '🚀'} Import</button>
        </div>
      )}
    </div>
  );
};


// ==================== BULK CONTACT IMPORT ====================
export const BulkContactImport = ({ token, onComplete }) => {
  const { language } = useLanguage();
  const [status, setStatus] = useState(null);
  const [file, setFile] = useState(null);
  const [columns, setColumns] = useState({ school: '', district: '', phone: '', email: '', principal: '', udise: '', affiliation: '' });
  const [uploading, setUploading] = useState(false);
  const [fileColumns, setFileColumns] = useState([]);

  useEffect(() => {
    loadStatus();
    const interval = setInterval(loadStatus, 3000);
    return () => clearInterval(interval);
  }, []);

  const loadStatus = async () => {
    const res = await api.get('/api/school-finder/bulk-contact-import-status', token);
    if (res.success) {
      setStatus(res.job);
      if (res.job?.status === 'completed' && onComplete) onComplete();
    }
  };

  const handleFileSelect = (e) => {
    const f = e.target.files[0];
    if (f) {
      setFile(f);
      const reader = new FileReader();
      reader.onload = (evt) => {
        const text = evt.target.result;
        const firstLine = text.split('\n')[0];
        const cols = firstLine.split(/[,\t]/).map(c => c.trim().replace(/"/g, ''));
        setFileColumns(cols);
      };
      reader.readAsText(f);
    }
  };

  const startImport = async () => {
    if (!file || !columns.school) { alert('Select file and school name column'); return; }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    const params = new URLSearchParams();
    params.append('school_name_col', columns.school);
    if (columns.district) params.append('district_col', columns.district);
    if (columns.phone) params.append('phone_col', columns.phone);
    if (columns.email) params.append('email_col', columns.email);
    
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/school-finder/bulk-contact-import?${params}`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData
      });
      const data = await res.json();
      if (data.success) loadStatus();
      else alert(data.error);
    } catch (err) { alert('Upload error'); }
    setUploading(false);
  };

  const stopImport = async () => {
    await api.post('/api/school-finder/bulk-contact-import-stop', {}, token);
    loadStatus();
  };

  return (
    <div className="bg-gradient-to-r from-violet-50 to-purple-50 rounded-xl p-4 border border-violet-200">
      <h3 className="font-semibold text-violet-800 mb-3">📥 Bulk Contact Import (Excel/CSV)</h3>
      
      {status?.status === 'running' ? (
        <div>
          <div className="w-full bg-gray-200 rounded-full h-3 mb-3">
            <div className="bg-violet-500 h-3 rounded-full" style={{ width: `${(status.processed / status.total_records) * 100}%` }}></div>
          </div>
          <div className="grid grid-cols-4 gap-2 text-center text-sm mb-2">
            <div><p className="font-bold">{status.processed}/{status.total_records}</p><p className="text-xs">Processed</p></div>
            <div><p className="font-bold text-green-600">{status.matched}</p><p className="text-xs">Matched</p></div>
            <div><p className="font-bold text-blue-600">{status.updated}</p><p className="text-xs">Updated</p></div>
            <div><p className="font-bold text-gray-600">{status.not_found}</p><p className="text-xs">Not Found</p></div>
          </div>
          <button onClick={stopImport} className="w-full py-2 bg-red-500 text-white rounded">⏹️ Stop</button>
        </div>
      ) : (
        <div className="space-y-3">
          <input type="file" accept=".xlsx,.xls,.csv" onChange={handleFileSelect} className="w-full text-sm" />
          {fileColumns.length > 0 && (
            <div className="grid grid-cols-2 gap-2 text-sm">
              <select value={columns.school} onChange={e => setColumns({...columns, school: e.target.value})} className="px-2 py-1 border rounded">
                <option value="">School Name *</option>
                {fileColumns.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <select value={columns.district} onChange={e => setColumns({...columns, district: e.target.value})} className="px-2 py-1 border rounded">
                <option value="">District</option>
                {fileColumns.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <select value={columns.phone} onChange={e => setColumns({...columns, phone: e.target.value})} className="px-2 py-1 border rounded">
                <option value="">Phone</option>
                {fileColumns.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <select value={columns.email} onChange={e => setColumns({...columns, email: e.target.value})} className="px-2 py-1 border rounded">
                <option value="">Email</option>
                {fileColumns.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          )}
          <button onClick={startImport} disabled={uploading || !file || !columns.school} className="w-full py-2 bg-violet-500 text-white rounded disabled:opacity-50">{uploading ? '⏳' : '🚀'} Start Import</button>
        </div>
      )}
    </div>
  );
};
